/**
 * @file GameBoard.tsx
 * @description
 * Main game board component for The Game. Renders the four piles,
 * highlights the current player’s turn, displays player hands (face-up for current, face-down for others),
 * positions players around the board, and provides controls for ending the turn.
 * Note: Host controls (Start Game, Kick, Reassign, End) are handled in the parent GameRoom component.
 *
 * @dependencies
 * - React: UI library
 * - Pile: Individual pile component
 * - Hand: Player hand component
 * - ../hooks/useGameState: For GameState interface definition and PileType
 *
 * @notes
 * - Expects a `state` prop matching the GameState interface.
 * - Wiring of socket events and game logic is handled in useGameState hook.
 * - Needs to receive playCard and endTurn handlers from the parent component (GameRoom).
 * - Implements player positioning logic based on player count and client's position.
 * - Integrates click-to-play logic.
 */

import React, { useMemo } from 'react'; // Import useMemo
import Pile from './Pile';
import Hand from './Hand';
import { GameState } from '../hooks/useGameState'; // Import GameState from the hook

// Define base player container styles
const basePlayerStyle: React.CSSProperties = {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
    border: '1px dashed #ccc',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9', // Light background for player area
    minWidth: '100px', // Ensure player area has minimum width
    minHeight: '120px', // Ensure player area has minimum height
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    zIndex: 10, // Ensure players are above the board background
    transition: 'all 0.3s ease-in-out', // Smooth transition for position changes
    textAlign: 'center', // Center text within player area
};

// Define specific player position styles
const playerPositions: Record<string, React.CSSProperties> = {
    bottom: { ...basePlayerStyle, bottom: '20px', left: '50%', transform: 'translateX(-50%)' },
    // MODIFICATION: Increased 'top' value for the top player to add space below the turn indicator
    top: { ...basePlayerStyle, top: '50px', left: '50%', transform: 'translateX(-50%)', flexDirection: 'column-reverse' }, // Hand above name
    left: { ...basePlayerStyle, left: '20px', top: '50%', transform: 'translateY(-50%)' },
    right: { ...basePlayerStyle, right: '20px', top: '50%', transform: 'translateY(-50%)' },
    'left-top': { ...basePlayerStyle, left: '20px', top: '20px' },
    'left-bottom': { ...basePlayerStyle, left: '20px', bottom: '20px' },
    'right-top': { ...basePlayerStyle, right: '20px', top: '20px' },
    'right-bottom': { ...basePlayerStyle, right: '20px', bottom: '20px' },
};


/**
 * Props for the GameBoard component.
 */
interface GameBoardProps {
  /** Current game state */
  state: GameState;
  /** Unique ID of the currently logged-in player (needed to identify their hand and turn) */
  currentPlayerId: string | null; // Assuming socket ID is used as player ID
  /** Handler for playing a card */
  onPlayCard: (pileId: string, card: number) => void;
  /** Handler for ending the current turn */
  onEndTurn: () => void;
  /** Function to validate if a card is playable on a specific pile */
  validateMove: (pileId: string, cardValue: number) => boolean;
   /** Boolean indicating if the current client is a spectator. */
   isSpectator: boolean; // Add isSpectator prop
}

/**
 * GameBoard component renders the main play area:
 * - Four piles
 * - Players positioned around the piles with their hands (current player's hand face-up)
 * - Turn indicator and end-turn button
 */
const GameBoard: React.FC<GameBoardProps> = ({
  state,
  currentPlayerId,
  onPlayCard,
  onEndTurn,
  validateMove,
  isSpectator, // Receive isSpectator prop
}) => {
  const { players, currentPlayerIndex, piles, hands, requiredPlays, deck } = state;

  // Find the player whose turn it is
  const currentTurnPlayer = players[currentPlayerIndex];
  const isMyTurn = currentPlayerId !== null && currentTurnPlayer?.id === currentPlayerId;

  // Find my index in the players array
  const myIndex = players.findIndex(p => p.id === currentPlayerId);
  const myHand = myIndex !== -1 ? hands[players[myIndex].id] || [] : []; // Get my hand if I am a player

  // State to track the currently selected card for playing (if using click-to-play)
  const [selectedCard, setSelectedCard] = React.useState<number | null>(null);

  // Memoize player positioning logic to avoid recalculating on every render
  const positionedPlayers = useMemo(() => {
      // Determine the index to use as the reference for positioning (client player or player 0 for spectator)
      const referenceIndex = (isSpectator && players.length > 0) ? 0 : myIndex;

      if (referenceIndex === -1 && players.length === 0) {
          // If not a player or spectator and no players, return empty
           return [];
      }
       if (referenceIndex === -1 && players.length > 0) {
           // This case should not happen if the user is guaranteed to be a player or spectator in a non-empty game
           console.warn("Client not found in players or spectators array, cannot position players.");
           // Fallback: Position relative to the first player? Or return empty?
           // Let's return empty for now, indicates an issue with state or logic.
           return [];
       }

      // Calculate positions relative to the reference index
      return players.map((player, index) => {
          const relativeIndex = (index - referenceIndex + players.length) % players.length;
          let position: string;
          switch (players.length) {
              case 1:
                  position = 'bottom'; // The single player is at the bottom
                  break;
              case 2:
                   // If 2 players, one is at the bottom (relative 0), one is at the top (relative 1)
                   position = relativeIndex === 0 ? 'bottom' : 'top';
                  break;
              case 3:
                   // If 3 players, bottom (0), left (1), right (2)
                   position = relativeIndex === 0 ? 'bottom' : (relativeIndex === 1 ? 'left' : 'right');
                  break;
              case 4:
                   // If 4 players, bottom (0), left (1), top (2), right (3)
                   position = relativeIndex === 0 ? 'bottom' : (relativeIndex === 1 ? 'left' : (relativeIndex === 2 ? 'top' : 'right'));
                  break;
              case 5:
                   // If 5 players, bottom (0), left-bottom (1), left-top (2), right-top (3), right-bottom (4)
                   position = relativeIndex === 0 ? 'bottom' : (relativeIndex === 1 ? 'left-bottom' : (relativeIndex === 2 ? 'left-top' : (relativeIndex === 3 ? 'right-top' : 'right-bottom')));
                  break;
              default:
                  // For player counts outside 1-5, positioning is undefined. Could place all at bottom?
                  // Or log a warning and place relative 0 at bottom?
                  console.warn("Unsupported number of players for positioning:", players.length);
                  position = relativeIndex === 0 ? 'bottom' : 'top'; // Simple fallback
          }
          return { ...player, position };
      });
  }, [players, myIndex, isSpectator]); // Recalculate if players, myIndex, or isSpectator changes


  const handleCardClick = (cardValue: number) => {
      if (isMyTurn && !isSpectator) { // Only allow if it's my turn AND I'm not a spectator
          // If no card is selected, select this one and show valid piles
          if (selectedCard === null) {
              setSelectedCard(cardValue);
               console.log(`Card ${cardValue} selected. Highlight potential piles.`);
          } else if (selectedCard === cardValue) {
              // If the same card is clicked again, deselect it
              setSelectedCard(null);
               console.log(`Card ${cardValue} deselected.`);
          } else {
              // If a different card is clicked, select the new one
              setSelectedCard(cardValue);
               console.log(`Card ${cardValue} re-selected.`);
          }
      } else {
          console.log("Cannot select card (not your turn or you are a spectator).");
      }
  };

   const handlePileClick = (pileId: string) => {
       // Only allow pile click if it's my turn, a card is selected, AND I'm not a spectator
       if (isMyTurn && selectedCard !== null && !isSpectator) {
           const cardToPlay = selectedCard;
            console.log(`Attempting to play selected card ${cardToPlay} on pile ${pileId}.`);
           // Validate the move before attempting to play
           if (validateMove(pileId, cardToPlay)) {
               onPlayCard(pileId, cardToPlay);
               setSelectedCard(null); // Clear selected card after a play attempt
           } else {
               console.warn(`Invalid move: Card ${cardToPlay} cannot be played on pile ${pileId}.`);
               // Optionally, show an error message on the UI (handled by useGameState chatError for now)
           }
       } else if (!isMyTurn) {
           console.log("Not your turn, cannot play card on pile.");
       } else if (isSpectator) {
            console.log("You are a spectator, cannot play card on pile.");
       } else { // selectedCard is null
           console.log("No card selected to play on pile.");
       }
   };


  // Determine which piles are valid targets for the selected card (if any)
  // Valid targets are only relevant if it's the current player's turn and a card is selected.
  const validDropTargets: string[] = useMemo(() => {
       const targets: string[] = [];
       if (isMyTurn && selectedCard !== null && piles && !isSpectator) { // Ensure piles object exists and is not spectator
           for (const pileId in piles) {
               if (Object.prototype.hasOwnProperty.call(piles, pileId)) {
                   // Use the validateMove function from props/hook
                   if (validateMove(pileId, selectedCard)) {
                       targets.push(pileId);
                   }
               }
           }
       }
       return targets;
  }, [isMyTurn, selectedCard, piles, validateMove, isSpectator]); // Recalculate if these dependencies change


  // Prepare piles for rendering - arrange them in a 2x2 layout visually
  const pileElements = Object.entries(piles).map(([pileId, pile]) => (
    <Pile
      key={pileId}
      id={pileId}
      type={pile.type}
      topValue={pile.top}
      isDropTarget={validDropTargets.includes(pileId)} // Highlight if it's a valid target for the selected card
       onClick={() => handlePileClick(pileId)} // Add click handler for pile
    />
  ));

  // Determine if the end turn button should be enabled
  // Enabled only if it's my turn AND I have played enough cards AND I am not a spectator
  const canEndTurn = isMyTurn && requiredPlays <= 0 && !isSpectator;


  return (
    <div style={{ padding: '1rem', position: 'relative', width: '100%', minHeight: 'calc(100vh - 80px)' /* Adjust height */ }}>
      {/* Turn Indicator and Required Plays */}
      <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', zIndex: 20 }}>
        <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
          Current Turn: {currentTurnPlayer ? currentTurnPlayer.name : 'Unknown Player'}
          {isMyTurn && <span style={{ color: 'green', marginLeft: '5px' }}>(Your Turn)</span>}
        </div>
         {/* Only show required plays if it's my turn and I'm a player */}
         {isMyTurn && !isSpectator && (
            <div style={{ fontSize: '1rem', color: requiredPlays > 0 ? 'orange' : 'green' }}>
                Cards to play this turn: <strong>{requiredPlays}</strong> | Deck size: {deck.length}
            </div>
         )}
      </div>


      {/* Player Areas */}
      {positionedPlayers.map(player => {
          // Determine if this is the current client's player
          const isClientPlayer = player.id === currentPlayerId;
          // Determine if this player's hand should be shown face up (only client player AND not spectator)
          const showHandFaceUp = isClientPlayer && !isSpectator;
          // Get the hand for this player (full hand for client, count for others)
          const hand = hands[player.id] || []; // Always get the hand array, Hand component handles rendering face-down if needed

          return (
              // Apply the player position style and the base style
              <div key={player.id} style={{ ...playerPositions[player.position] }} aria-label={`Player ${player.name} area`}>
                   {/* Player Name and Status */}
                  <div style={{ fontWeight: player.id === currentTurnPlayer?.id ? 'bold' : 'normal', marginBottom: '5px' }}>
                      {player.name} {player.id === state.hostId && '(Host)'} {isClientPlayer && '(You)'}
                  </div>
                  {/* Player Hand */}
                   {/* Only render hand if it's the current player or a player's hand with cards (for count) */}
                   {showHandFaceUp || (hand.length > 0 && !isSpectator) ? ( // Don't render empty hands for other players or spectator hands
                         <Hand
                            cards={hand} // Pass the hand array
                            onCardClick={showHandFaceUp ? handleCardClick : undefined} // Only allow clicking on current player's cards
                            isCurrentPlayer={showHandFaceUp} // Tell Hand if this is the client's hand (determines face-up/down)
                            // Pass the player's name to the Hand component for face-down rendering
                            playerName={player.name}
                            piles={piles} // Pass piles for potential 'isPlayable' logic in Hand (if implemented)
                            // validatePlay={validateMove} // Pass validation for Hand (if implemented)
                            selectedCard={selectedCard} // Pass selected card for highlighting in Hand
                         />
                     ) : null}
              </div>
          );
      })}

      {/* Piles - Centered on the board */}
      <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'grid', // Use grid for 2x2 layout
            gridTemplateColumns: 'repeat(2, min-content)', // Two columns, size to content
            gridTemplateRows: 'repeat(2, min-content)', // Two rows, size to content
            gap: '20px', // Space between piles
            zIndex: 5, // Below players, above background
            justifyItems: 'center', // Center items within grid cells
            alignItems: 'center', // Center items within grid cells
       }} aria-label="Game piles">
        {/* Render piles in a specific order if needed, or rely on Object.entries order */}
        {/* Example: asc1, asc2, desc1, desc2 */}
        {/* The order from Object.entries might not be fixed. If order matters, sort or list explicitly. */}
         {Object.entries(piles)
             .sort(([idA], [idB]) => idA.localeCompare(idB)) // Sort by id (asc1, asc2, desc1, desc2)
             .map(([pileId, pile]) => (
                <Pile
                  key={pileId}
                  id={pileId}
                  type={pile.type}
                  topValue={pile.top}
                  isDropTarget={validDropTargets.includes(pileId)} // Highlight if it's a valid target for the selected card
                   onClick={() => handlePileClick(pileId)} // Add click handler for pile
                />
            ))
         }
      </div>


      {/* End Turn Control - Positioned near the current player (bottom)
      {isMyTurn && !isSpectator && ( // Only show end turn if it's my turn AND I'm a player
          <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 20 }}>
            <button
              onClick={onEndTurn} // Use the handler from props
              disabled={!canEndTurn} // Disable if not enough cards played
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                borderRadius: '0.5rem',
                cursor: canEndTurn ? 'pointer' : 'not-allowed',
                opacity: canEndTurn ? 1 : 0.5,
              }}
            >
              End Turn
            </button>
          </div>
      )} */}

        {/* Spectator Count (Optional, can be integrated elsewhere) */}
        {state.spectators.length > 0 && (
             <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '0.9rem', color: '#555', zIndex: 10 }}>
                  Spectators: {state.spectators.length}
             </div>
        )}


    </div>
  );
};

export default GameBoard;